import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect } from 'react';
import { z } from 'zod';
import { supabase } from '@munawb/supabase';

// مخطط بسيط بدون token — Supabase يتحقق من الجلسة تلقائياً
const schema = z
  .object({
    new_password:     z
      .string()
      .min(8, 'كلمة المرور يجب أن تكون 8 أحرف على الأقل')
      .regex(/[A-Z]/, 'يجب أن تحتوي على حرف كبير واحد على الأقل')
      .regex(/[0-9]/, 'يجب أن تحتوي على رقم واحد على الأقل'),
    confirm_password: z.string(),
  })
  .refine((d) => d.new_password === d.confirm_password, {
    message: 'كلمات المرور غير متطابقة',
    path: ['confirm_password'],
  });

type FormValues = z.infer<typeof schema>;

export default function ConfirmResetPasswordPage() {
  const [status, setStatus]       = useState<'idle' | 'success' | 'error'>('idle');
  const [serverMsg, setServerMsg] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);

  // استخرج الـ session من الـ hash عند التحميل
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token')) {
      // Supabase يعالج الـ hash تلقائياً عند استدعاء getSession
      supabase.auth.getSession().then(({ data }) => {
        if (data.session) setSessionReady(true);
        // امسح الـ hash من الـ URL بعد المعالجة
        window.history.replaceState(null, '', window.location.pathname);
      });
    } else {
      // لا يوجد hash — تحقق من session موجودة مسبقاً
      supabase.auth.getSession().then(({ data }) => {
        if (data.session) setSessionReady(true);
      });
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormValues) => {
    setServerMsg(null);
    try {
      // Supabase يستخدم الجلسة الموجودة من رابط الاسترداد
      const { error } = await supabase.auth.updateUser({
        password: data.new_password,
      });

      if (error) throw error;

      setStatus('success');

      // تسجيل الخروج بعد التغيير لإجبار المستخدم على الدخول مجدداً
      setTimeout(() => supabase.auth.signOut(), 2000);

    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'حدث خطأ غير متوقع';
      setServerMsg(msg);
      setStatus('error');
    }
  };

  /* ── Success ── */
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950 p-4">
        <div className="glass-panel w-full max-w-md rounded-3xl p-8 shadow-2xl text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-base font-black text-slate-800 dark:text-white">تم تغيير كلمة المرور</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-bold">
            يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة.
          </p>
          <a href="/" className="btn-primary inline-block px-6 py-2.5 text-sm rounded-xl">
            تسجيل الدخول
          </a>
        </div>
      </div>
    );
  }

  /* ── No session ── */
  if (!sessionReady && status === 'idle') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950 p-4">
        <div className="glass-panel w-full max-w-md rounded-3xl p-8 shadow-2xl text-center space-y-4">
          <div className="w-8 h-8 mx-auto rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
          <p className="text-sm font-bold text-slate-500 dark:text-slate-400">جارٍ التحقق من الرابط...</p>
        </div>
      </div>
    );
  }

  /* ── Form ── */
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950 p-4">
      <div className="glass-panel w-full max-w-md rounded-3xl p-8 shadow-2xl">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shadow-md">
            <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white">كلمة مرور جديدة</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-bold mt-1">
            أدخل كلمة المرور الجديدة لحسابك
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>

          {/* New Password */}
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              كلمة المرور الجديدة
            </label>
            <input
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              className="input-field"
              dir="ltr"
              {...register('new_password')}
            />
            {errors.new_password && (
              <p className="text-red-500 text-xs font-bold mt-1">{errors.new_password.message}</p>
            )}
            <p className="text-xs text-slate-400 dark:text-slate-500 font-medium mt-1.5">
              8 أحرف على الأقل، حرف كبير، ورقم
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">
              تأكيد كلمة المرور
            </label>
            <input
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              className="input-field"
              dir="ltr"
              {...register('confirm_password')}
            />
            {errors.confirm_password && (
              <p className="text-red-500 text-xs font-bold mt-1">{errors.confirm_password.message}</p>
            )}
          </div>

          {status === 'error' && serverMsg && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 text-center">
              <p className="text-red-600 dark:text-red-400 text-sm font-bold">{serverMsg}</p>
            </div>
          )}

          <button type="submit" className="btn-primary w-full py-3 text-base" disabled={isSubmitting}>
            {isSubmitting ? (
              <><span className="spinner border-white/30 border-t-white" /> جارٍ الحفظ...</>
            ) : (
              'حفظ كلمة المرور الجديدة'
            )}
          </button>

        </form>
      </div>
    </div>
  );
}
